import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import InputTextField from "@/Components/Form/InputTextField";
import MultiSelectDropdown from "@/Components/Form/MultiSelectDropdown";
import { useUser } from "../../../Context/UserContext";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import {
  useCreateOrganizationMutation,
  useLazyGetOrganizationByIdQuery,
} from "../../../redux/Organization/CreateNewCustomer/NewCustomers";
import { useGetCustomertypeMasterQuery } from "../../../redux/Masters/Masters";
import SuccessDialog from "../../../Components/Dialog/SuccessDialog";
import { BiSolidError } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";

export default function FilterBar() {
  const { customerId } = useParams();
  const formData = useUser((state) => state.formData);
  const setFormData = useUser((state) => state.setFormData);
  const setLoading = useUser((s) => s.setLoading);
  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createOrganization, { isLoading }] = useCreateOrganizationMutation();
  const setCurrentTabType = useUser((s) => s.setCurrentTabType);
  const setIsRequiredFieldFilled = useUser((s) => s.setIsRequiredFieldFilled);
  const navigate = useNavigate();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [taskValue, setTaskValue] = useState(""); // state for Task input
  const [selectedEntities, setSelectedEntities] = useState([]);
  const { data: customerTypeOptions = [] } = useGetCustomertypeMasterQuery();
  const [getOrgById, { data: orgData, isFetching, error }] =
    useLazyGetOrganizationByIdQuery();
  const filterTrigger = useUser((s) => s.filterTrigger);

  useEffect(() => {
    if (!customerId) return;

    console.log("🔥 Edit Mode → Fetching Customer:", customerId);
    getOrgById(customerId);
  }, [customerId]);
  const filterHandled = useRef(false);

  useEffect(() => {
    if (!filterTrigger) return;

    if (filterHandled.current) {
      console.log("🟡 Filter already handled → skipping");
      return;
    }

    filterHandled.current = true; // LOCK

    const urlId = customerId;

    if (urlId) {
      console.log("🔵 EDIT MODE → SKIP CREATE");
      // runUpdateAPI();
      return;
    } else {
      console.log("🟢 NEW MODE → CREATE");
      runFilterBarCreateAPI();
    }

    // 🔐 UNLOCK after 300ms (safe)
    setTimeout(() => {
      filterHandled.current = false;
      useUser.getState().setFilterTrigger(false);
    }, 300);
  }, [filterTrigger]);

  /** ---------------------------
   *  LOCAL DROPDOWN OPTIONS
   * --------------------------- */
  const dropdownOptions = customerTypeOptions.map((item) => ({
    label: item.name,
    value: item.id,
  }));

  /** -----------------------------------------
   *  FIXED HANDLERS FOR NAME + ALIAS
   * ----------------------------------------- */
  const handleNameChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      customer: { ...prev.customer, name: value },
    }));
  };

  const handleAliasChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      customer: {
        ...prev.customer,
        alias: value,
      },
    }));
  };

  useEffect(() => {
    const name = formData?.customer?.name?.trim();
    const alias = formData?.customer?.alias?.trim();
    const entity = formData?.customer?.entityTypes || [];

    const isFilled = !!name || !!alias || entity.length > 0;

    setIsRequiredFieldFilled(isFilled);
    if (isFilled) {
      setCurrentTabType("form"); // treat current tab as form
    }
  }, [formData.customer]);

  const handleEntityTypeChange = (selectedValues) => {
    const selectedObjects = dropdownOptions.filter((opt) =>
      selectedValues.includes(opt.value)
    );

    setFormData((prev) => ({
      ...prev,
      customer: {
        ...prev.customer,
        entityTypes: selectedObjects,
      },
    }));
  };
  // ⭐ PREFILL FORM WHEN GET API RETURNS DATA
  useEffect(() => {
    if (!orgData) return;

    console.log("🟢 PREFILLING FROM GET API:", orgData);
    console.log(" PREFILLING FROM GET API:", formData);

    setFormData((prev) => ({
      ...prev,
      customer: {
        customerId: orgData.customerId ?? "",
        name: orgData.customerName ?? "",
        alias: orgData.aliseName ?? "",
        entityTypes: (orgData.userTypes ?? []).map((u) => ({
          label: u.name,
          value: u.id,
        })),
      },
    }));
  }, [orgData]);

  const runFilterBarCreateAPI = async () => {
    const payload = {
      customerName: formData.customer.name,
      aliseName: formData.customer.alias,
      userTypesIds: formData.customer.entityTypes.map((item) => item.value),
    };

    try {
      setLoading(true);

      const response = await createOrganization(payload).unwrap();
      const customerId = response?.data; // ⭐ get here so it's available everywhere

      if (customerId) {
        localStorage.setItem("customerId", customerId);

        // ⭐ STORE into Zustand formData
        setFormData((prev) => ({
          ...prev,
          customer: {
            ...prev.customer,
            customerId,
            name: formData.customer.name,
            alias: formData.customer.alias,
            entityTypes: formData.customer.entityTypes,
          },
        }));
      }

      // ⭐ SUCCESS BLOCK
      if (response?.statusCode === 200) {
        setSuccessDialogConfig({
          label: "Success",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {response?.message || "Organization created successfully"}
            </strong>
          ),
          buttonText: "OK",
          IconComponent: BiSolidError,
        });
        setShowSuccessDialog(true);

        // ⭐⭐ Navigate AFTER dialog closes (recommended UX)
        setTimeout(() => {
          navigate(`/organization/create-customer/${customerId}`);
        }, 400);

        return response;
      }

      // ❌ FAILURE BLOCK
      else {
        setSuccessDialogConfig({
          label: "Failure",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {response?.message || "Something went wrong"}
            </strong>
          ),
          buttonText: "OK",
          status: "error",
          icon: BiSolidError,
        });
        setShowSuccessDialog(true);

        return response;
      }
    } catch (err) {
      setSuccessDialogConfig({
        label: "Failure",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {err?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        icon: BiSolidError,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "var(--gray-0)",
        border: "1px solid var(--brand-200)",
        borderRadius: 2,
        p: 1,
        gap: 2,
        display: "flex",
      }}
    >
      <InputTextField
        label="Name"
        placeholder="Enter your Name"
        width="50%"
        value={formData?.customer?.name || ""}
        onChange={(e) => handleNameChange(e.target.value)}
      />

      <InputTextField
        label="Alias"
        placeholder="Enter Alias Name"
        width="45%"
        value={formData?.customer?.alias || ""}
        onChange={(e) => handleAliasChange(e.target.value)}
      />

      <MultiSelectDropdown
        placeholder="Select Entity Type"
        value={formData?.customer?.entityTypes?.map((e) => e.value) ?? []}
        options={dropdownOptions}
        onChange={handleEntityTypeChange}
        width={"30%"}
        sx={{ minWidth: 220, mt: 4.1 }}
      />
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
        }}
        {...successDialogConfig}
      />
    </Box>
  );
}
